import { LAUNCHER_PAGE_SIZE, paginateWithLeadingSlot, type LauncherSlot } from "./launcher-pages";

type GameSlot = Extract<LauncherSlot, { kind: "local" | "external" | "catalog-entry" }>;

function isGame(slot: LauncherSlot): slot is GameSlot {
  return slot.kind === "local" || slot.kind === "external" || slot.kind === "catalog-entry";
}

function identity(slot: GameSlot): string {
  if (slot.kind === "local") return `local:${slot.manifest.id}`;
  if (slot.kind === "external") return `external:${slot.game.url}`;
  return `catalog:${slot.entry.catalogId}`;
}

/**
 * Shuffle the entire pool BEFORE pagination, never just the visible page.
 * Call only when a browse view opens; retain the returned pages for paging.
 * Neither the input arrays nor any game/launch metadata are changed.
 */
export function shuffleBrowsePages(
  pages: readonly (readonly LauncherSlot[])[],
  previous: readonly (readonly LauncherSlot[])[] = [],
  random: () => number = Math.random,
): readonly (readonly LauncherSlot[])[] {
  const games = pages.flat().filter(isGame);
  for (let index = games.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [games[index], games[swap]] = [games[swap]!, games[index]!];
  }

  const capacity = LAUNCHER_PAGE_SIZE - 1;
  const prior = (previous[0] ?? []).filter(isGame).map(identity);
  const first = games.slice(0, capacity).map(identity);
  // With more than a page available, avoid showing the same selection again.
  // For a small category, at least change its order. A singleton cannot move.
  const repeated = first.length === prior.length && (games.length > capacity
    ? first.every((key) => prior.includes(key))
    : first.every((key, index) => key === prior[index]));
  if (games.length > 1 && repeated) {
    const shift = games.length > capacity ? capacity : 1;
    games.push(...games.splice(0, shift));
  }

  return paginateWithLeadingSlot({ kind: "categories" }, games);
}
